// Which messages of a thread this app shows, and where its spam went.
//
// The api files a message the spam filter or the reader called spam as a row
// in namespace 'spam' of its thread (NM-SPAM-21), the way trash is a row in
// namespace 'trash'. This app has no Spam folder and gets none (DEC-SPAM-11):
// Spam lives in Newtmail. A thread with an inbox row is still listed, so its
// spam rows are left out of the thread view here and only counted, and a
// thread that is spam only is never listed, because no folder this app asks
// the api for (FOLDER_NAMESPACES, ?row_namespace=) contains it.
import { isSafeLink, normaliseUrl } from './mail-html.js';

export const SPAM_NAMESPACE = 'spam';
export const TRASH_NAMESPACE = 'trash';

// The folders this app lists, and so the only row namespaces it asks for.
export const FOLDER_NAMESPACES = Object.freeze(['inbox', 'sent', 'drafts', 'trash']);

export const isFolderNamespace = (namespace) => FOLDER_NAMESPACES.includes(namespace);

const isMessage = (m) => m !== null && typeof m === 'object';

// The messages of `emails` to show in the view of folder `namespace`, oldest
// first by position, and how many of the thread's messages are spam. A spam
// view shows spam; every other view hides it. `hiddenIds` are messages
// deleted in this session that a refresh has not caught up with.
export const threadMessages = (emails, namespace, hiddenIds = new Set()) => {
  if (!Array.isArray(emails)) return { messages: [], spamCount: 0 };
  const live = emails
    .filter(isMessage)
    .filter((m) => !hiddenIds.has(m.id))
    .sort((a, b) => a.position - b.position);
  const hideSpam = namespace !== SPAM_NAMESPACE;
  const spamCount = hideSpam ? live.filter((m) => m.namespace === SPAM_NAMESPACE).length : 0;
  const messages = live.filter((m) => {
    if (hideSpam && m.namespace === SPAM_NAMESPACE) return false;
    return namespace === TRASH_NAMESPACE ? m.namespace === TRASH_NAMESPACE : m.namespace !== TRASH_NAMESPACE;
  });
  return { messages, spamCount };
};

// The message a thread opens on in the view of folder `namespace`: its newest
// message filed in that folder, the one the list shows for the thread. null
// when it has none, and then the list does not show the thread either. The
// list, the toolbar's arrows and the thread view all pick it here. The arrows
// used to take the thread's first row, and the api returns a thread's rows
// highest position first (tables/views.py, order_by('-position')), so in a
// thread whose newest reply went to Junk that first row is the spam: its
// sender became the header and the reply's recipient.
export const latestFolderMail = (emails, namespace) => {
  if (!Array.isArray(emails)) return null;
  return emails
    .filter(isMessage)
    .filter((m) => m.namespace === namespace)
    .reduce((latest, current) => (
      !latest || new Date(current.date) > new Date(latest.date) ? current : latest
    ), null);
};

// The mail the thread view is about when `active` was opened: the header,
// the reply's recipients, In-Reply-To and Forward all read it. That is
// `active` itself, unless it is a spam row outside a spam view; then it is
// the thread's own message for this folder (latestFolderMail), or nothing.
// So a spam row that reaches `active` by any road still never names its
// sender on the page or receives the reply.
export const viewedMail = (active, emails, namespace) => {
  if (!isMessage(active)) return null;
  if (active.namespace !== SPAM_NAMESPACE || namespace === SPAM_NAMESPACE) return active;
  return latestFolderMail(emails, namespace);
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Newtmail's Spam view for `business`, or null. `base` is the build's
// NEXT_PUBLIC_NEWTMAIL_URL: unset until Newtmail is live, so the notice is
// text only until then. Only a plain https origin is accepted (no path,
// credentials, query or fragment), after the app's own link check, so a
// mistyped or hostile value can never become a javascript:, data: or
// look-alike link; the business must be a uuid, so it cannot add a path.
export const newtmailSpamUrl = (base, business) => {
  if (typeof base !== 'string' || typeof business !== 'string' || !UUID.test(business)) return null;
  const candidate = normaliseUrl(base);
  if (!candidate || !isSafeLink(candidate)) return null;
  let url;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || !url.hostname || url.username || url.password
    || url.pathname !== '/' || url.search || url.hash || candidate.includes('?') || candidate.includes('#')) {
    return null;
  }
  return `${url.origin}/b/${business.toLowerCase()}/spam`;
};
